import { useState } from 'react';
import { requestPasswordReset } from '../../features/auth/api/user-reset';
import { parseBackendErrors } from '@/shared/lib/backend-errors';
import { exitToLogin } from '@/shared/lib/session-exit';
import { Button } from '@/shared/ui/button';
import { FormField } from '@/shared/ui/form-field';
import { LanguageToggle } from '@/shared/ui/language-toggle';
import { Logo } from '@/shared/ui/logo';
import { useToast } from '@/shared/hooks/use-toast';
import { useLanguage } from '@/shared/hooks/use-language';

const FORGOT_PASSWORD_FIELD_MAP: Record<string, string> = {
  'user_email': 'email',
};

function ForgotPassword() {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setFieldErrors({ email: t("forgotPassword.emailRequired") || 'Email is required' });
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setFieldErrors({});
    setSuccess(false);

    try {
      const result = await requestPasswordReset(email.trim());

      if (result.success) {
        setSuccess(true);
        toast({
          title: t("forgotPassword.successTitle"),
          description: t("forgotPassword.successDescription"),
        });
      } else {
        const errorMessage = result.error || 'Failed to send password reset email';
        const { fieldErrors: backendFieldErrors, generalError } = parseBackendErrors(
          result.validationError,
          FORGOT_PASSWORD_FIELD_MAP,
        );
        if (Object.keys(backendFieldErrors).length > 0) {
          setFieldErrors(backendFieldErrors);
          setError(generalError);
        } else {
          setError(errorMessage);
        }
        toast({
          title: t("forgotPassword.errorTitle"),
          description: errorMessage,
          variant: "destructive",
        });
      }
    } catch (error) {
      const errorMessage = 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
      toast({
        title: t("forgotPassword.errorTitle"),
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn">
      <div className="w-full max-w-md p-8">
        <div className="mb-8 text-center">
          <Logo className="justify-center mb-4" />
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            {t("forgotPassword.title")}
          </h1>
          <p className="text-muted-foreground">
            {t("forgotPassword.subtitle")}
          </p>
        </div>

        <div className="bg-card p-8 rounded-2xl border border-border card-hover">
          {success ? (
            <div className="space-y-6">
              <div className="bg-green-500/10 border border-green-500/20 text-green-500 px-4 py-3 rounded-md text-sm">
                <p className="font-semibold mb-2">{t("forgotPassword.successMessage")}</p>
                <p className="text-sm">
                  {t("forgotPassword.successDetails")}
                </p>
              </div>
              <Button
                onClick={() => exitToLogin()}
                className="w-full"
                variant="outline"
              >
                {t("forgotPassword.backToLogin")}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleResetRequest} className="space-y-6">
              {error && (
                <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <FormField
                id="email"
                type="email"
                label={t("forgotPassword.email")}
                placeholder={t("forgotPassword.emailPlaceholder")}
                value={email}
                onChange={(value) => {
                  setEmail(value);
                  if (fieldErrors.email) {
                    setFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.email;
                      return next;
                    });
                  }
                }}
                error={fieldErrors.email}
                disabled={isLoading}
                required
                autoComplete="email"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isLoading) {
                    handleResetRequest(e);
                  }
                }}
              />

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground btn-arrow"
                disabled={isLoading}
              >
                {isLoading ? t("forgotPassword.sending") : t("forgotPassword.submit")}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <button
              onClick={() => exitToLogin()}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              {t("forgotPassword.backToLogin")}
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-border">
            <LanguageToggle />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
